import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import InputDate from "core/inputs/input-date/InputDate";
import FafErrorDot from "./FafErrorDot";
import FafField from "./FafField";
import FafCandidateSearch from "./FafCandidateSearch";
import { localeForIntl } from "utils/fafDashboardHelper";
import {
  formatDayMonth,
  hasFieldError,
  nextQualifRank,
  qualifDateInputId,
  qualifReviewFieldErrors,
  QUALIF_STATUSES,
  QUALIF_STATUS_NEW_CANDIDATE,
  QUALIF_STATUS_REQUALIF,
} from "utils/fafHelper";

// Banner cell text: next rank for the requalif cell, icon + label for the swap.
const StatusLabel = ({ status, nextRank }) => {
  if (status === QUALIF_STATUS_NEW_CANDIDATE)
    return (
      <>
        <span className="faf-seg-icon" aria-hidden="true" />
        <span className="faf-seg-label">
          <FormattedMessage
            id="tools_manager_faf_qualif_new_candidate"
            defaultMessage="Nv candidat"
          />
        </span>
      </>
    );
  return (
    <span className="faf-seg-label">
      {status === QUALIF_STATUS_REQUALIF ? `Q${nextRank}` : status}
    </span>
  );
};

StatusLabel.propTypes = {
  status: PropTypes.string.isRequired,
  nextRank: PropTypes.number.isRequired,
};

// A qualif to rule this week: progress, status banner, then the field its status requires.
const QualifReviewCard = ({
  qualif: q,
  needTitle,
  review,
  onChange,
  showError,
  lang,
}) => {
  const locale = localeForIntl(useIntl());
  const dormant = !!q.is_dormant;
  const errors = qualifReviewFieldErrors(review);
  const flagged = showError && !dormant;
  const invalid = flagged && hasFieldError(errors);
  const nextRank = nextQualifRank(q.rank);
  const rankDate = formatDayMonth({ isoDate: q.rank_date, locale });
  const meta = [needTitle, q.candidate_email].filter(Boolean).join(" · ");

  return (
    <div
      className={`faf-list-card faf-list-card--wrap ${
        invalid ? "faf-list-card--error" : ""
      } ${dormant ? "faf-list-card--dormant" : ""}`}
      data-testid={`faf-qualif-review-${q.id}`}
    >
      <div className="faf-list-card-main">
        <div className="faf-list-card-title">
          <h4>
            {q.candidate_first_name} {q.candidate_last_name}
          </h4>
          {q.rank != null && (
            <span className="faf-qualif-progress">
              {[`Q${q.rank}`, rankDate].filter(Boolean).join(" · ")}
            </span>
          )}
          {q.candidate_order > 1 && (
            <span className="faf-qualif-order">
              <FormattedMessage
                id="tools_manager_faf_qualif_candidate_order"
                defaultMessage="{n}e candidat"
                values={{ n: q.candidate_order }}
              />
            </span>
          )}
          {invalid && (
            <FafErrorDot
              label={{
                id: "tools_manager_faf_qualif_review_dot_label",
                defaultMessage: "Qualification à statuer",
              }}
              errorMessage="tools_manager_faf_qualif_review_error_field"
            />
          )}
        </div>
        {meta && <div className="faf-list-card-meta">{meta}</div>}
        {dormant && rankDate && (
          <div className="faf-list-card-meta">
            <FormattedMessage
              id="tools_manager_faf_qualif_dormant"
              defaultMessage="Attendue le {date}"
              values={{ date: rankDate }}
            />
          </div>
        )}
      </div>
      <div className="faf-seg">
        {QUALIF_STATUSES.map((s) => (
          <button
            type="button"
            key={s}
            disabled={dormant}
            className={`faf-seg-btn ${s.toLowerCase()} ${
              review.status === s ? "on" : ""
            }`}
            data-testid={`faf-qualif-review-${q.id}-status-${s.toLowerCase()}`}
            onClick={() => onChange({ status: s })}
          >
            <StatusLabel status={s} nextRank={nextRank} />
          </button>
        ))}
      </div>
      {!dormant && review.status === QUALIF_STATUS_REQUALIF && (
        <FafField
          className="faf-qualif-extra"
          label={{
            id: "tools_manager_faf_qualif_date_rank",
            defaultMessage: "Date de la {n}e qualification",
            values: { n: nextRank },
          }}
          required
          invalid={flagged && errors.qualification_date}
          errorLabel={{
            id: "tools_manager_faf_qualif_date_label",
            defaultMessage: "Date de qualification",
          }}
          errorMessage="tools_manager_faf_qualif_date_required"
        >
          <InputDate
            id={qualifDateInputId(q.id)}
            name={`faf-qualif-review-${q.id}-date`}
            value={review.qualification_date || ""}
            lang={lang}
            // Browsing months must not commit a date on its own.
            onMonthChange={undefined}
            onYearChange={undefined}
            // Focused on a failed submit: keeps the calendar from opening off screen.
            preventOpenOnFocus
            onChange={(e) => onChange({ qualification_date: e.target.value })}
          />
        </FafField>
      )}
      {!dormant && review.status === QUALIF_STATUS_NEW_CANDIDATE && (
        <FafField
          className="faf-qualif-extra"
          label={{
            id: "tools_manager_faf_qualif_candidate",
            defaultMessage: "Candidat",
          }}
          required
          invalid={flagged && errors.new_candidate}
          errorMessage="tools_manager_faf_candidate_required"
        >
          <FafCandidateSearch
            candidate={review.new_candidate}
            onChange={(candidate) => onChange({ new_candidate: candidate })}
            dataTestId={`faf-qualif-review-${q.id}-candidate`}
          />
        </FafField>
      )}
    </div>
  );
};

QualifReviewCard.propTypes = {
  qualif: PropTypes.shape({
    id: PropTypes.number.isRequired,
    candidate_first_name: PropTypes.string,
    candidate_last_name: PropTypes.string,
    candidate_email: PropTypes.string,
    rank: PropTypes.number,
    rank_date: PropTypes.string,
    candidate_order: PropTypes.number,
    is_dormant: PropTypes.bool,
  }).isRequired,
  needTitle: PropTypes.string,
  review: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  showError: PropTypes.bool,
  lang: PropTypes.string,
};

export default QualifReviewCard;
